// src/components/admin/UpdateProduct.tsx
import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { Product } from "../../types";
import { useCategoriesStore } from "../../store/categoriesStore";
import { useBrandsStore, useIngredientsStore, useProblemsStore, useSkinTypesStore } from "../../store/adminStore";
import { updateProductWithImages } from "../../services/productService";
import ProductFormFields from "../product/ProductFormFields";
import ImageUpload, { ImageType } from "../product/ImageUpload";
import Notification from "../toast/Notification";
import { Container, Paper, Typography, Button, CircularProgress } from "@mui/material";
import { prepareRequestData } from "../../utils/productUtils";

export interface UpdateProductProps {
  productToUpdate: Product | null;
  setActiveTab: (tab: string) => void;
  onBackToList?: () => void;
}

const UpdateProduct: React.FC<UpdateProductProps> = ({ productToUpdate, setActiveTab, onBackToList }) => {
  const { categories, fetchCategories } = useCategoriesStore();
  const { brands } = useBrandsStore();
  const { ingredients } = useIngredientsStore();
  const { problems } = useProblemsStore();
  const { skinTypes } = useSkinTypesStore();

  const { register, handleSubmit, setValue, control, reset, formState: { errors } } = useForm<Product>();

  const { fields, append, remove } = useFieldArray({ control, name: "properties" });

  const [categoryId, setCategoryId] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [nameProduct, setNameProduct] = useState("");
  const [sku, setSku] = useState("");
  const [discount, setDiscount] = useState("");
  const [searchBrands, setSearchBrands] = useState("");
  // Изображения теперь типа ImageType[] (File или string)
  const [images, setImages] = useState<ImageType[]>([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{ id: number,text: string; status: "success" | "error" | "info" } | null>(null);

  useEffect(() => {
    fetchCategories().catch((error) => console.error("Ошибка загрузки категорий:", error));
    if (productToUpdate) {
      // Предзаполняем форму данными продукта
      reset(productToUpdate);
      setValue("name", productToUpdate.name || "");
      setNameProduct(productToUpdate.name || "");
      setPrice(productToUpdate.price.toString());
      setDescription(productToUpdate.description || "");
      setCategoryId(productToUpdate.category_id.toString());
      setDiscount(productToUpdate.discount ? productToUpdate.discount.toString() : "");
      setValue("sku", productToUpdate.sku || "");
      setSku(productToUpdate.sku || "");
      if (productToUpdate.brand && productToUpdate.brand.name) {
        setSearchBrands(productToUpdate.brand.name);
      }
      // Если продукт уже содержит изображения (предполагается, что images – массив строк URL)
      if (productToUpdate.images && productToUpdate.images.length > 0) {
        setImages(productToUpdate.images);
        // Найдем индекс cover_image в массиве images
        const idx = productToUpdate.images.findIndex((img) => img === productToUpdate.cover_image);
        setCoverIndex(idx >= 0 ? idx : 0);
      }
    }
  }, [fetchCategories, productToUpdate, reset, setValue]);

  const onSubmit: SubmitHandler<Product> = async (data) => {
    if (images.length === 0) {
      setNotification({ id: Date.now(), text: "Пожалуйста, добавьте хотя бы одно изображение.", status: "error" });
      return;
    }
    try {
      setIsLoading(true);
      // Преобразуем данные из формата Product в формат ProductPayload
      let preparedData = prepareRequestData(data);
      // Если в товаре уже были изображения, и они не менялись через форму, сохраняем их.
      if (productToUpdate && productToUpdate.images && productToUpdate.images.length > 0) {
        // Если пользователь не изменил изображения для слотов, оставляем старый массив.
        // Обратите внимание: если пользователь загрузил хотя бы один новый файл, то в соответствующих индексах
        // функция updateProductWithImages заменит значение.
        preparedData.images = productToUpdate.images;
        // Аналогично, если cover_image не установлен в подготовленных данных, сохраняем старую обложку.
        if (!preparedData.cover_image) {
          preparedData.cover_image = productToUpdate.cover_image;
        }
      }
      const response = await updateProductWithImages(
        preparedData,  // данные товара в формате ProductPayload
        images,        // массив изображений типа ImageType[]
        coverIndex     // индекс обложки
      );
      setNotification({ id: Date.now(), text: `Товар "${response.product.name}" успешно обновлён!`, status: "success" });
      if (onBackToList) {
        onBackToList();
      } else {
        setActiveTab("adminStats");
      }
    } catch (error) {
      console.error("Ошибка при обновлении товара:", error);
      setNotification({ id: Date.now(), text: "Не удалось обновить товар", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ padding: 3, marginTop: 4, marginBottom: 4 }}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <Typography variant="h5" gutterBottom sx={{ margin: 0 }}>
            Редактирование товара: {productToUpdate?.name || ""}
          </Typography>
          {onBackToList && (
            <Button variant="outlined" size="small" onClick={onBackToList}>
              К списку товаров
            </Button>
          )}
        </div>
        {isLoading && <CircularProgress />}
        {notification && <Notification id={notification.id || Date.now()} text={notification.text} status={notification.status} duration={15000} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <ProductFormFields
            register={register}
            errors={errors}
            control={control}
            setValue={setValue}
            fields={fields}
            append={append}
            remove={remove}
            nameProduct={nameProduct}
            setNameProduct={setNameProduct}
            categories={categories}
            brands={brands}
            ingredients={ingredients}
            problems={problems}
            skinTypes={skinTypes}
            price={price}
            setPrice={setPrice}
            sku={sku}
            setSku={setSku}
            searchBrands={searchBrands}
            setSearchBrands={setSearchBrands}
            description={description}
            setDescription={setDescription}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            discount={discount}
            setDiscount={setDiscount}
          />
          <ImageUpload images={images} setImages={setImages} coverIndex={coverIndex} setCoverIndex={setCoverIndex} />
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ marginTop: 2 }}>
            Обновить товар
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default UpdateProduct;
